import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin, Gift, Users, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useSiteSettings } from '../context/SiteSettingsContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CompleteFooter = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { settings } = useSiteSettings();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email });
      
      toast.success('Welcome to the BOXVERRA family! ✨', {
        description: 'Check your email for your exclusive 15% discount code.',
        duration: 8000
      });
      setEmail('');
    } catch (error) {
      if (error.response?.data?.message?.includes('already subscribed')) {
        toast.info('You\'re already part of our exclusive list!', {
          description: 'Check your inbox for your discount code.'
        });
      } else {
        toast.error('Something went wrong', {
          description: 'Please try again later.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A]">
      {/* Newsletter - Luxury Gold */}
      <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37]">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Gift className="h-10 w-10 text-[#0A0A0A]" />
              <span className="bg-[#0A0A0A] text-[#D4AF37] font-bold text-sm px-4 py-1 rounded-full uppercase tracking-wide">
                Exclusive Offer
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-[#0A0A0A]">
              Get 15% Off Your First Order
            </h3>
            <p className="text-xl text-[#0A0A0A]/80 mb-3 font-body">
              Join our exclusive list for early access to new collections & VIP deals
            </p>
            <div className="flex items-center justify-center gap-2 mb-8 text-[#0A0A0A]/70">
              <Users className="h-5 w-5" />
              <span className="font-body">Join 20,000+ luxury collectors</span>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-[#0A0A0A] border-0 text-white placeholder-[#888888] focus:ring-2 focus:ring-[#D4AF37] font-body text-lg px-6 py-6 rounded-full"
              />
              <Button 
                type="submit" 
                disabled={isLoading}
                className="bg-[#0A0A0A] hover:bg-[#1A1A1A] text-[#D4AF37] font-bold px-10 py-6 font-body text-lg rounded-full shadow-lg hover:scale-105 transition-transform disabled:opacity-70 border-2 border-[#0A0A0A] hover:border-[#D4AF37]"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Get My 15% Off'}
              </Button>
            </form>
            <p className="text-sm text-[#0A0A0A]/60 mt-4 font-body">
              No spam, unsubscribe anytime. Your privacy is our priority.
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 mb-12">
          {/* Logo & Contact */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-lg flex items-center justify-center shadow-lg shadow-[#D4AF37]/30">
                <span className="text-[#0A0A0A] text-2xl font-bold font-heading">B</span>
              </div>
              <div>
                <h4 className="text-2xl font-bold text-white font-heading tracking-wide">{settings?.business_name || 'BOXVERRA'}</h4>
                <p className="text-sm text-[#D4AF37] font-body tracking-widest uppercase">{settings?.tagline || 'Luxury Collectibles'}</p>
              </div>
            </div>
            <p className="text-base text-[#888888] mb-6 leading-relaxed font-body">
              Premium collectible toys and luxury home goods for discerning collectors. Experience excellence in every piece.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-8">
              <a href={`mailto:${settings?.email || 'support@boxverra.com'}`} className="flex items-center gap-3 text-[#888888] hover:text-[#D4AF37] transition-colors font-body">
                <Mail className="h-5 w-5" />
                {settings?.email || 'support@boxverra.com'}
              </a>
              <a href={`tel:${settings?.phone?.replace(/\D/g, '') || '18001234567'}`} className="flex items-center gap-3 text-[#888888] hover:text-[#D4AF37] transition-colors font-body">
                <Phone className="h-5 w-5" />
                {settings?.phone || '+1 (800) 123-4567'}
              </a>
              <div className="flex items-start gap-3 text-[#888888] font-body">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{settings?.address ? `${settings.address.street}, ${settings.address.city}, ${settings.address.state} ${settings.address.zip}` : 'Beverly Hills, CA 90210'}</span>
              </div>
            </div>
            
            {/* Social - Instagram, Facebook, Twitter */}
            <div className="flex gap-4">
              <a href={settings?.social_links?.instagram || 'https://instagram.com'} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-[#141414] flex items-center justify-center text-[#888888] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all hover:scale-110 hover:shadow-lg hover:shadow-[#D4AF37]/30 border border-[#D4AF37]/20">
                <Instagram className="h-7 w-7" />
              </a>
              <a href={settings?.social_links?.facebook || 'https://facebook.com'} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-[#141414] flex items-center justify-center text-[#888888] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all hover:scale-110 hover:shadow-lg hover:shadow-[#D4AF37]/30 border border-[#D4AF37]/20">
                <Facebook className="h-7 w-7" />
              </a>
              <a href={settings?.social_links?.twitter || 'https://twitter.com'} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-[#141414] flex items-center justify-center text-[#888888] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all hover:scale-110 hover:shadow-lg hover:shadow-[#D4AF37]/30 border border-[#D4AF37]/20">
                <Twitter className="h-7 w-7" />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5 font-heading">Shop</h4>
            <ul className="space-y-3 text-base font-body list-none">
              <li><Link to="/shop" className="text-[#888888] hover:text-[#D4AF37] transition-colors">All Products</Link></li>
              <li><Link to="/shop?collection=new-arrivals" className="text-[#888888] hover:text-[#D4AF37] transition-colors">New Arrivals</Link></li>
              <li><Link to="/shop?collection=best-sellers" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Best Sellers</Link></li>
              <li><Link to="/shop?collection=sale" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Sale</Link></li>
              <li><Link to="/shop" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Categories</Link></li>
            </ul>
          </div>

          {/* Customer Care */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5 font-heading">Customer Care</h4>
            <ul className="space-y-3 text-base font-body list-none">
              <li><Link to="/contact" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Contact Us</Link></li>
              <li><Link to="/shipping-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Shipping Info</Link></li>
              <li><Link to="/return-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Returns & Exchanges</Link></li>
              <li><Link to="/account" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Track Order</Link></li>
              <li><Link to="/account" className="text-[#888888] hover:text-[#D4AF37] transition-colors">My Account</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5 font-heading">Company</h4>
            <ul className="space-y-3 text-base font-body list-none">
              <li><Link to="/about" className="text-[#888888] hover:text-[#D4AF37] transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Blog</Link></li>
              <li><Link to="/terms" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/privacy-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-[#D4AF37]/20 pt-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Payment icons */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#888888] mr-3 font-body">We accept:</span>
              <div className="flex items-center gap-3">
                {/* Visa */}
                <div className="w-14 h-9 bg-white rounded flex items-center justify-center">
                  <svg viewBox="0 0 48 32" className="h-5 w-auto">
                    <path fill="#1A1F71" d="M19.5 10.3l-2.9 11.4h-2.4l2.9-11.4h2.4zm12.1 7.4l1.3-3.5.7 3.5h-2zm2.7 4h2.2l-1.9-11.4h-2c-.5 0-.9.3-1.1.7l-3.8 10.7h2.7l.5-1.4h3.3l.1 1.4zm-6.7-3.7c0-3-4.1-3.2-4.1-4.5 0-.4.4-.8 1.3-.9.4 0 1.6-.1 2.9.5l.5-2.4c-.7-.3-1.6-.5-2.8-.5-3 0-5.1 1.6-5.1 3.9 0 1.7 1.5 2.6 2.7 3.2 1.2.6 1.6.9 1.6 1.4 0 .8-.9 1.1-1.8 1.1-1.5 0-2.4-.4-3.1-.7l-.5 2.5c.7.3 2 .6 3.3.6 3.2 0 5.3-1.6 5.3-4 0-3.1-4.2-3.3-4.2-4.6v-.1zM12 10.3L8 21.7H5.2l-2-9.1c-.1-.5-.2-.6-.6-.8C1.8 11.3.6 11 .6 11l.1-.7h4.4c.6 0 1 .4 1.1 1l1.1 5.8 2.7-6.8h2z"/>
                  </svg>
                </div>
                {/* Mastercard */}
                <div className="w-14 h-9 bg-white rounded flex items-center justify-center">
                  <svg viewBox="0 0 48 32" className="h-5 w-auto">
                    <circle cx="18" cy="16" r="10" fill="#EB001B"/>
                    <circle cx="30" cy="16" r="10" fill="#F79E1B"/>
                    <path fill="#FF5F00" d="M24 8.5a10 10 0 0 0-3.5 7.5 10 10 0 0 0 3.5 7.5 10 10 0 0 0 3.5-7.5 10 10 0 0 0-3.5-7.5z"/>
                  </svg>
                </div>
                {/* American Express */}
                <div className="w-14 h-9 bg-[#006FCF] rounded flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">AMEX</span>
                </div>
                {/* PayPal */}
                <div className="w-14 h-9 bg-white rounded flex items-center justify-center">
                  <svg viewBox="0 0 48 32" className="h-4 w-auto">
                    <path fill="#253B80" d="M17.5 7h5.4c3.6 0 5 1.5 4.6 4.5-.5 3.6-2.7 5.5-6.2 5.5h-1.4c-.4 0-.8.3-.9.8l-.7 4.2h-3l2.2-15z"/>
                    <path fill="#179BD7" d="M21.5 9h5.4c3.6 0 5 1.5 4.6 4.5-.5 3.6-2.7 5.5-6.2 5.5h-1.4c-.4 0-.8.3-.9.8l-.7 4.2h-3l2.2-15z"/>
                  </svg>
                </div>
                {/* Apple Pay */}
                <div className="w-14 h-9 bg-black rounded flex items-center justify-center border border-[#333]">
                  <span className="text-white text-[10px] font-medium"> Pay</span>
                </div>
              </div>
            </div>

            {/* Security badges */}
            <div className="flex items-center gap-4 text-[#888888]">
              <div className="flex items-center gap-2 text-sm font-body">
                <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                <span>Secure Checkout</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-body">
                <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                <span>SSL Encrypted</span>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center mt-10 pt-8 border-t border-[#D4AF37]/20">
            <p className="text-sm text-[#888888] font-body mb-4">
              © {new Date().getFullYear()} BOXVERRA. All rights reserved. Crafted with excellence.
            </p>
            <div className="flex gap-6 justify-center text-sm font-body flex-wrap">
              <Link to="/terms" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Privacy</Link>
              <Link to="/shipping-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Shipping</Link>
              <Link to="/return-policy" className="text-[#888888] hover:text-[#D4AF37] transition-colors">Returns</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default CompleteFooter;
